import cn from "classnames";
import { LocaleLink } from "~/lib/components/link";
import { useLocale } from "~/i18n/i18n.client";
import {
  IArticleProps,
  ISectionProps,
  ISubsectionProps,
} from "~/lib/types/article.type";
import { transformPathKebabCase } from "~/lib/utils/helper/transformPath";
import { useTranslation } from "~/lib/utils/store/useTranslation";
import DetailsLayout from "../../layout/DetailsLayout";
import { List } from "../../ui/List";

interface IPageProps {
  articles?: IArticleProps[];
  sectionProps?: ISectionProps;
  selectedSubsection?: ISubsectionProps;
}

const getArticleLink = ({ article }: { article: IArticleProps }) => {
  return `/article/${transformPathKebabCase(article.title, `${article.id}/`)}`;
};

const FeatureLayout = ({
  articles = [],
  sectionProps,
  selectedSubsection,
}: IPageProps) => {
  const { t } = useTranslation();
  const locale = useLocale();

  const renderArticles = () => {
    if (!articles.length || !selectedSubsection?.articles?.length) {
      return;
    }

    const renderHighlightSection = selectedSubsection.articles
      .filter((highlight) =>
        articles.some((article) =>
          article.label_names
            .map((name) => name.toLowerCase())
            .includes(highlight.value)
        )
      )
      .map((highlight, idx) => {
        const foundArticles = articles.filter((article) =>
          article.label_names
            .map((name) => name.toLowerCase())
            .includes(highlight.value)
        );

        const NUM_OF_VISIBLE_ARTICLE = 8;
        const isVisibleShowAllButton =
          foundArticles.length > NUM_OF_VISIBLE_ARTICLE;

        return (
          <div
            key={highlight.value}
            className={cn(
              "md:p-0 p-5",
              idx !== 0 ? "border-t-8 border-gray-500 md:border-0" : "pt-0"
            )}
          >
            <div className="mb-5 font-title text-black font-semibold text-xl">
              {highlight.label?.[`${locale}`] ||
                highlight.label.en ||
                String(highlight.label)}
            </div>
            <List
              isLoading={false}
              data={foundArticles
                .slice(0, NUM_OF_VISIBLE_ARTICLE)
                .map((article) => ({
                  label: article.title,
                  value: getArticleLink({ article }),
                }))}
            />
            {isVisibleShowAllButton && (
              <LocaleLink
                to={`/topics/${sectionProps?.sectionId}/${highlight.value}`}
              >
                <div className="w-full pt-4 mt-4 border-t border-gray-400 text-sm text-brand hover:underline cursor-pointer">
                  {t("seeAllArticles", {
                    count: foundArticles.length - NUM_OF_VISIBLE_ARTICLE,
                  })}
                </div>
              </LocaleLink>
            )}
          </div>
        );
      });

    return (
      <div className="md:p-10 md:border-gray-500 md:border-[1px] md:rounded-xl md:flex md:flex-col md:gap-14">
        {renderHighlightSection}
      </div>
    );
  };

  return (
    <>
      <div className="p-0 mx-auto w-full 2xl:px-[128px] md:px-[50px] xl:px-[100px] max-w-screen-md md:max-w-8xl md:p-5">
        <DetailsLayout selectedSubsection={selectedSubsection}>
          {renderArticles()}
        </DetailsLayout>
      </div>
    </>
  );
};

export default FeatureLayout;
